/* ============================================================================
 * Movatic WMS — Frontend shared component | CesarFilho 932048016
 * Arquivo: src/components/ScanResultCard.tsx
 * ----------------------------------------------------------------------------
 * Cartão de resultado de consulta/scan, reutilizado no Desktop e Mobile.
 * Mostra: descrição, SKU/EAN/UOM, imagens, total, por localização,
 *         recepção pendente e POs abertos.
 * ========================================================================== */

import { StockScan } from '@/lib/hooks/useStockScan'
import { Image as ImageIcon, Package, MapPin, Truck } from 'lucide-react'

const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 })
const fmtQty = (v?: number) => (typeof v === 'number' ? nf.format(v) : '—')
const fmtDate = (s?: string) => {
    if (!s) return '—'
    const d = new Date(s)
    return isNaN(+d) ? s : d.toLocaleDateString()
}

type Props = {
    data?: StockScan | null
    loading?: boolean
    error?: unknown
    showImages?: boolean
}

export default function ScanResultCard({ data, loading, error, showImages = true }: Props) {
    if (loading) {
        return (
            <div className="card p-3">
                <div className="text-sm opacity-70">Consultando…</div>
            </div>
        )
    }
    if (error) {
        return (
            <div className="card p-3 border-red-500/40">
                <div className="text-sm text-red-600">Erro ao consultar: {String((error as any)?.message ?? '—')}</div>
            </div>
        )
    }
    if (!data) return null

    if (!data.found) {
        return (
            <div className="card p-3">
                <div className="text-sm">Nenhum produto encontrado para <b>{data.query}</b>.</div>
            </div>
        )
    }

    const p = data.product

    return (
        <div className="card p-3 grid gap-3">
            {/* Cabeçalho */}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-semibold truncate">{p?.name ?? '—'}</div>
                    <div className="text-xs text-neutral-500 mt-1 space-x-3">
                        <span><b>SKU:</b> {p?.code ?? '—'}</span>
                        <span><b>EAN:</b> {p?.ean ?? '—'}</span>
                        <span><b>UM:</b> {p?.uom ?? '—'}</span>
                    </div>
                </div>
                <div className="px-2 py-1 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs flex items-center gap-1">
                    <Package size={14} /> <span>Total</span> <b>{fmtQty(data.total?.qty)}</b> {data.total?.uom ?? 'UN'}
                </div>
            </div>

            {/* Imagens */}
            {showImages && (
                <div className="flex gap-2 overflow-auto">
                    {(data.images ?? []).slice(0, 2).map((img, idx) => (
                        <img
                            key={idx}
                            src={img.url}
                            alt={img.alt ?? `img ${idx + 1}`}
                            className="h-28 w-auto rounded-md border border-neutral-200 dark:border-neutral-800 object-cover"
                            onError={(e: any) => { e.currentTarget.style.display = 'none' }}
                        />
                    ))}
                    {(!data.images || data.images.length === 0) && (
                        <div className="h-28 w-40 rounded-md border border-dashed border-neutral-300 dark:border-neutral-700 flex items-center justify-center text-neutral-500 text-xs">
                            <ImageIcon size={14} className="mr-1" /> sem imagens
                        </div>
                    )}
                </div>
            )}

            {/* Por localização */}
            <div>
                <div className="text-xs font-semibold mb-1 flex items-center gap-1"><MapPin size={14} /> Por localização</div>
                {data.locations?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {data.locations.map((l, i) => (
                            <div key={i} className="rounded-md border border-neutral-200 dark:border-neutral-800 px-2 py-1 text-sm flex items-center justify-between">
                                <span className="font-mono">{l.location}</span>
                                <span className="font-medium">{fmtQty(l.qty)}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-neutral-500">Sem saldos por localização.</div>
                )}
            </div>

            {/* Recepção & POs */}
            {(data.receiving?.on_dock_qty || (data.receiving?.open_po?.length ?? 0) > 0) && (
                <div className="grid gap-2">
                    {!!data.receiving?.on_dock_qty && data.receiving.on_dock_qty > 0 && (
                        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/60 px-2 py-1 text-sm">
                            Em recepção: <b>{fmtQty(data.receiving.on_dock_qty)}</b> {p?.uom ?? data.total?.uom ?? 'UN'}
                        </div>
                    )}
                    {(data.receiving?.open_po?.length ?? 0) > 0 && (
                        <div>
                            <div className="text-xs font-semibold mb-1 flex items-center gap-1"><Truck size={14} /> Pedidos em aberto</div>
                            <div className="grid gap-1">
                                {data.receiving!.open_po!.map((po, i) => (
                                    <div key={i} className="rounded-md border border-neutral-200 dark:border-neutral-800 px-2 py-1 text-xs flex items-center justify-between">
                                        <span>PO <b>{po.po}</b>{po.eta ? ` • ETA ${fmtDate(po.eta)}` : ''}</span>
                                        <span className="font-medium">{fmtQty(po.qty)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
